import _uniqueId from "lodash/uniqueId";
import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Stick from 'react-stick';
import { LegacyButton, LegacyText, Tile, useStyles } from '@signavio/ui';
import { AutocompleteContext, useSelectionIndex } from './AutocompleteContext';
import DataSources, { listboxId } from './DataSources';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function Autocomplete({
  children,
  placeholder,
  autoFocus = false,
  inline = false,
  id = '',
  emptyHint,
  readOnly = false,
  triggerComponent: TriggerComponent = Trigger,
  onSelect = () => {},
  onCollapse,
  onChange,
  ...rest
}) {
  const [autocompleteId] = useState(() => _uniqueId('autocomplete'));
  const trigger = useRef(null);
  const ConditionalDataSources = useMemo(() => ({
    children
  }) => /*#__PURE__*/_jsx(DataSources, {
    ownerId: autocompleteId,
    children: children
  }), [autocompleteId]);
  const activationChecks = useRef({});
  const [queryString, setQueryString] = useState(null);
  const [activeValue, setActiveValue] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const styles = useStyles(defaultStyles, {}, {
    '&inline': inline,
    '&attached': !inline
  });
  const [dataSourceDescriptors, registerDataSource, deregisterDataSource] = useDataSourceDescriptors();
  const [selectionIndex, selectPrevious, selectNext, selectIndex, resetSelection] = useSelectionIndex(dataSourceDescriptors, activationChecks);
  useEffect(() => {
    if (activeValue == null) {
      resetSelection();
    }
  }, [activeValue, resetSelection]);
  useEffect(() => {
    if (!isOpen) {
      setQueryString(null);
    }
  }, [isOpen]);
  useLayoutEffect(() => {
    if (isOpen && trigger.current) {
      trigger.current.focus();
    }
  }, [isOpen]);
  const [selectValue, registerSelectCallback, deregisterSelectCallback] = useSelectValue(onSelect);
  const handleKeyUp = event => {
    if (event.key === 'ArrowUp') {
      selectPrevious();
    } else if (event.key === 'ArrowDown') {
      selectNext();
    } else if (event.key === 'Enter' && activeValue) {
      handleSelect(...activeValue, event);
    }
  };
  const handleChange = query => {
    setQueryString(query);
    resetSelection();
    if (onChange) {
      // @ts-ignore - SIGPG-3238: TextInput onChange prop expects a handler with a nullable<string> argument
      onChange(query);
    }
  };
  const handleSelect = useCallback((type, item, event) => {
    selectValue(type, item);
    if (inline) {
      return;
    }
    setQueryString(null);
    if (event.key === 'Enter') {
      return;
    }
    setIsOpen(false);
    if (onCollapse) {
      onCollapse();
    }
  }, [inline, onCollapse, selectValue]);
  const handleCollapse = () => {
    setIsOpen(false);
    if (onCollapse) {
      onCollapse();
    }
  };
  const hasNoResults = dataSourceDescriptors.every(dataSourceDescriptor => dataSourceDescriptor.numberOfItems === 0);
  return /*#__PURE__*/_jsx(AutocompleteContext.Provider, {
    value: {
      queryString,
      registerDataSource,
      deregisterDataSource,
      selectionIndex,
      dataSourceDescriptors,
      activeValue,
      activationChecks,
      selectIndex,
      setActiveValue,
      selectValue: handleSelect,
      registerSelectCallback,
      deregisterSelectCallback
    },
    children: inline ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(TriggerComponent, {
        ...rest,
        isExpanded: true,
        inline: true,
        id: id,
        ref: trigger,
        role: "combobox",
        "aria-owns": listboxId(autocompleteId),
        "aria-expanded": isOpen ? 'true' : 'false',
        autoFocus: autoFocus // eslint-disable-line jsx-a11y/no-autofocus
        ,
        placeholder: placeholder,
        readOnly: readOnly,
        value: queryString,
        onChange: handleChange,
        onKeyUp: handleKeyUp,
        onRequestCollapse: handleCollapse,
        onRequestExpand: () => setIsOpen(true)
      }), /*#__PURE__*/_jsxs("div", {
        ...styles('suggestions'),
        children: [typeof children === 'function' ? children(ConditionalDataSources, queryString, !hasNoResults) : /*#__PURE__*/_jsx(DataSources, {
          ownerId: autocompleteId,
          children: children
        }), hasNoResults && /*#__PURE__*/_jsx("div", {
          ...styles('emptyHint'),
          children: emptyHint
        })]
      })]
    }) : /*#__PURE__*/_jsx(Stick, {
      sameWidth: true,
      onClickOutside: () => {
        if (document.activeElement === trigger.current) {
          return;
        }
        handleCollapse();
      },
      node: isOpen && /*#__PURE__*/_jsxs("div", {
        ...styles('suggestions'),
        children: [typeof children === 'function' ? children(ConditionalDataSources, queryString, !hasNoResults) : /*#__PURE__*/_jsx(DataSources, {
          ownerId: autocompleteId,
          children: children
        }), hasNoResults && /*#__PURE__*/_jsx("div", {
          ...styles('emptyHint'),
          children: emptyHint
        })]
      }),
      children: /*#__PURE__*/_jsx(TriggerComponent, {
        ...rest,
        id: id,
        role: "combobox",
        "aria-owns": listboxId(autocompleteId),
        "aria-expanded": isOpen ? 'true' : 'false',
        ref: trigger,
        isExpanded: isOpen,
        inline: false,
        placeholder: placeholder,
        readOnly: readOnly,
        autoFocus: autoFocus // eslint-disable-line jsx-a11y/no-autofocus
        ,
        value: queryString,
        onChange: handleChange,
        onKeyUp: handleKeyUp,
        onRequestCollapse: handleCollapse,
        onRequestExpand: () => {
          setIsOpen(true);
        }
      })
    })
  });
}
const Trigger = /*#__PURE__*/forwardRef(({
  inline,
  isExpanded,
  autoFocus,
  placeholder,
  value,
  readOnly,
  onChange,
  onRequestCollapse,
  onRequestExpand,
  onKeyUp
}, ref) => {
  if (inline) {
    return /*#__PURE__*/_jsx(LegacyText, {
      role: "combobox",
      inputRef: ref,
      disabled: readOnly,
      autoFocus: autoFocus // eslint-disable-line jsx-a11y/no-autofocus
      ,
      value: value,
      placeholder: placeholder,
      onChange: onChange,
      onKeyUp: onKeyUp
    });
  }
  return /*#__PURE__*/_jsx(Tile, {
    toolbar: !readOnly && /*#__PURE__*/_jsx(LegacyButton, {
      "data-testid": "toggle-autocomplete-dropdown",
      icon: isExpanded ? 'dropdown-close' : 'dropdown-open',
      onClick: event => {
        event.stopPropagation();
        if (isExpanded) {
          onRequestCollapse();
        } else {
          onRequestExpand();
        }
      }
    }),
    children: /*#__PURE__*/_jsx(LegacyText, {
      role: "combobox",
      inputRef: ref,
      disabled: readOnly,
      autoFocus: autoFocus // eslint-disable-line jsx-a11y/no-autofocus
      ,
      value: value,
      placeholder: placeholder,
      onChange: onChange,
      onKeyUp: onKeyUp,
      onFocus: onRequestExpand
    })
  });
});
function useSelectValue(onSelect) {
  const selectCallbacks = useRef([]);
  const registerSelectCallback = useCallback(onSelect => {
    selectCallbacks.current = [...selectCallbacks.current, onSelect];
  }, []);
  const deregisterSelectCallback = useCallback(onSelect => {
    selectCallbacks.current = selectCallbacks.current.filter(callback => callback !== onSelect);
  }, []);
  const selectValue = useCallback((type, item) => {
    if (onSelect) {
      onSelect(type, item);
    }
    selectCallbacks.current.forEach(callback => callback(type, item));
  }, [onSelect]);
  return [selectValue, registerSelectCallback, deregisterSelectCallback];
}
const useDataSourceDescriptors = () => {
  const [dataSourceDescriptors, setDataSourceDescriptors] = useState([]);
  const registerDataSource = useCallback((type, index, numberOfItems) => {
    setDataSourceDescriptors(currentDataSourceDescriptors =>
    // sorting the data source descriptors at this point isn't
    // strictly required but helps us avoid this at a lot
    // of other places.
    [...currentDataSourceDescriptors, {
      type,
      index,
      numberOfItems
    }].sort((a, b) => a.index - b.index));
  }, []);
  const deregisterDataSource = useCallback(type => {
    setDataSourceDescriptors(currentDataSourceDescriptors => currentDataSourceDescriptors.filter(dataSourceDescriptor => dataSourceDescriptor.type !== type));
  }, []);
  return [dataSourceDescriptors, registerDataSource, deregisterDataSource];
};
const defaultStyles = ({
  color,
  spacing,
  shadow
}) => ({
  suggestions: {
    backgroundColor: color.surface,
    maxHeight: 300,
    overflowY: 'auto'
  },
  emptyHint: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: spacing.xxsmall,
    marginBottom: spacing.xxsmall
  },
  '&attached': {
    suggestions: {
      ...shadow.menu,
      borderRadius: 2
    }
  }
});
export default Autocomplete;